import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "./ToastProvider";

interface NetworkStatusContextValue {
  isOnline: boolean;
}

const NetworkStatusContext = createContext<NetworkStatusContextValue | undefined>(undefined);

function getInitialOnlineState() {
  if (typeof navigator === "undefined") {
    return true;
  }
  return navigator.onLine ?? true;
}

export function NetworkStatusProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { warning, success, dismiss } = useToast();
  const offlineToast = useRef<string | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(getInitialOnlineState);

  useEffect(() => {
    if (!isOnline) {
      if (!offlineToast.current) {
        offlineToast.current = warning(t("network.offline_message"), {
          duration: 0,
          id: "network-offline",
        });
      }
      return;
    }

    if (offlineToast.current) {
      dismiss(offlineToast.current);
      offlineToast.current = null;
      success(t("network.online_message"), { duration: 3000 });
    }
  }, [dismiss, isOnline, success, t, warning]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleOnline = () => {
      setIsOnline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const value = useMemo<NetworkStatusContextValue>(
    () => ({
      isOnline,
    }),
    [isOnline],
  );

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
}

export function useNetworkStatus() {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error("useNetworkStatus must be used within a NetworkStatusProvider");
  }
  return context;
}
